import React, { useMemo } from 'react';
import { Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import type { AccommodationListingDTO } from '@shared/types/Accommodations.types';
import { SkeletonText } from '@shared/components/Skeleton';
import { StarRating } from '@apps/stays/components/Ratings';
import type { PointOfInterest } from './TravelLogistics';
import { TravelLogistics } from './TravelLogistics';
import type { Maybe } from '@graphql/generated-base';

type HeaderBlockProps = Readonly<{
  listing: AccommodationListingDTO;
  pointOfInterest: Maybe<PointOfInterest>;
  isMobileOrTablet: boolean;
  redirectUrl?: string;
  // Whether the name links to the PDP. Defaults to `!isSoldOut`; treatment enables it for sold-out tiles.
  isLinkEnabled?: boolean;
  /**
   * Rendered opposite the star rating on the first row (desktop shape only).
   *
   * For the cash-fund card, whose comp sets the `% off` badge beside the stars rather than above the
   * price. The badge itself stays the caller's, so the shared price column is untouched.
   */
  trailingBadge?: React.ReactNode;
}>;

const NameAndStarRatingPanel = ({ children }: { children: React.ReactNode }) => {
  return (
    <Flex
      gap={5}
      flexWrap={'wrap'}
      alignItems={'center'}
    >
      {children}
    </Flex>
  );
};

const HeaderBlock = ({ listing, pointOfInterest, isMobileOrTablet, redirectUrl, isLinkEnabled, trailingBadge }: HeaderBlockProps) => {
  const addressText = useMemo(() => {
    if (listing.address) {
      return [listing.address.street, listing.address.city].filter(Boolean).join(', ');
    }

    return null;
  }, [listing]);
  const hasStarRating = typeof listing.starRating === 'number' && listing.starRating > 0;
  const isSoldOut = listing.type === 'standardHotel' && listing.isSoldOutForSelectedDates;
  const linkEnabled = isLinkEnabled ?? !isSoldOut;

  const listingNameProps = {
    overflow: 'hidden',
    display: '-webkit-box',
    typographyVariant: 'hed2',
    style: {
      WebkitLineClamp: 2,
      WebkitBoxOrient: 'vertical',
    },
  } as const;

  if (!isMobileOrTablet) {
    return (
      <Flex
        flexDirection="column"
        rowGap={2}
      >
        {/* Rendered only when it has something in it. An always-on row would add the parent's
            `rowGap` above the name for every unrated listing and room block, in every scenario.
            With a badge but no stars, `flex-end` keeps the badge over the price column. */}
        {(hasStarRating || trailingBadge) && (
          <Flex
            alignItems="center"
            justifyContent={hasStarRating ? 'space-between' : 'flex-end'}
            columnGap={4}
          >
            {hasStarRating && <StarRating rating={listing.starRating as number} />}
            {trailingBadge}
          </Flex>
        )}
        <TextV2
          {...listingNameProps}
          _hover={{ textDecoration: linkEnabled && redirectUrl ? 'underline' : 'none' }}
        >
          {listing.name}
        </TextV2>
        {listing.address && (
          <TextV2
            typographyVariant="label2"
            color="mono10"
          >
            {addressText}
          </TextV2>
        )}
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      rowGap={3}
    >
      <NameAndStarRatingPanel>
        <TextV2
          {...listingNameProps}
          maxWidth={pxToRem(260)}
        >
          {listing.name}
        </TextV2>
        {typeof listing.starRating === 'number' && listing.starRating > 0 && <StarRating rating={listing.starRating} />}
      </NameAndStarRatingPanel>
      {pointOfInterest && (
        <TravelLogistics
          useShortWording={true}
          pointOfInterest={pointOfInterest}
          listing={listing}
        />
      )}
    </Flex>
  );
};

HeaderBlock.Skeleton = ({ isMobileOrTablet, showTrailingBadge }: { isMobileOrTablet: boolean; showTrailingBadge?: boolean }) => {
  if (!isMobileOrTablet) {
    return (
      <Flex
        flexDirection="column"
        rowGap={2}
      >
        {/* Mirrors the `trailingBadge` row above, so a tile that places a badge beside its stars
            does not shift its whole content down a line once prices land. */}
        <Flex
          alignItems="center"
          justifyContent="space-between"
          columnGap={4}
        >
          <StarRating.Skeleton />
          {showTrailingBadge && (
            <SkeletonText
              rows={1}
              skeletonHeight={20}
              width={58}
            />
          )}
        </Flex>
        <Flex
          flexDirection="column"
          rowGap={3}
        >
          <SkeletonText
            rows={1}
            skeletonHeight={20}
            width={175}
          />
          <SkeletonText
            rows={1}
            skeletonHeight={20}
            width={80}
          />
        </Flex>
        <SkeletonText
          rows={1}
          skeletonHeight={18}
          width={160}
        />
      </Flex>
    );
  }

  return (
    <Flex
      flexDirection="column"
      rowGap={3}
    >
      <NameAndStarRatingPanel>
        <SkeletonText
          rows={1}
          skeletonHeight={20}
          width={175}
        />
        <StarRating.Skeleton />
      </NameAndStarRatingPanel>
      <TravelLogistics.Skeleton />
    </Flex>
  );
};

export { HeaderBlock };
